import React from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { ShoppingCart, Menu, X, Sparkles, Accessibility } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { BRAND } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/id-card", label: "ID Card" },
  { to: "/resources", label: "Resources" },
  { to: "/insurance", label: "Insurance" },
  { to: "/donate", label: "Donate" },
  { to: "/fundraisers", label: "Fundraisers" },
  { to: "/community", label: "Community" },
];

export default function Layout() {
  const { user, cartCount, a11y, setA11y, logout } = useApp();
  const [open, setOpen] = React.useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="rainbow-strip h-1" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
            <img src={BRAND.mascot} alt="Zi Zi's World" className="w-11 h-11 rounded-full object-cover ring-2 ring-pink-200" />
            <div className="leading-tight">
              <div className="font-display font-bold text-lg text-pink-600 pink-glow tracking-wide">ZI ZI'S WORLD</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Autism Support Store</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 mx-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive ? "bg-pink-50 text-pink-600" : "text-slate-700 hover:text-pink-600 hover:bg-pink-50/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="a11y-trigger" variant="ghost" size="icon" aria-label="Accessibility settings" className="rounded-full">
                  <Accessibility className="h-5 w-5 text-purple-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Accessibility</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem checked={a11y.text === "large"} onCheckedChange={(v) => setA11y({ ...a11y, text: v ? "large" : "" })}>
                  Larger text
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={a11y.text === "xlarge"} onCheckedChange={(v) => setA11y({ ...a11y, text: v ? "xlarge" : "" })}>
                  Extra large text
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={!!a11y.dyslexic} onCheckedChange={(v) => setA11y({ ...a11y, dyslexic: v })}>
                  Dyslexia-friendly font
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={!!a11y.highContrast} onCheckedChange={(v) => setA11y({ ...a11y, highContrast: v })}>
                  High contrast
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={!!a11y.reduceMotion} onCheckedChange={(v) => setA11y({ ...a11y, reduceMotion: v })}>
                  Reduce motion
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/cart" data-testid="nav-cart" className="relative p-2 rounded-full hover:bg-pink-50">
              <ShoppingCart className="h-5 w-5 text-slate-700" />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-account" className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-pink-50">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-pink-200 grid place-items-center text-pink-700 font-bold">{user.name?.[0] || "U"}</div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/dashboard" data-testid="menu-dashboard">My Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/membership" data-testid="menu-membership">Membership ({user.points} pts)</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/fundraisers/new" data-testid="menu-start-fundraiser">Start a Fundraiser</Link></DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="nav-login" className="btn-outline-pink hidden sm:inline-flex text-sm">
                Sign in
              </Link>
            )}

            <button className="lg:hidden p-2" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="mobile-menu">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <nav className="lg:hidden border-t border-pink-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 gap-2">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-pink-50">{n.label}</Link>
              ))}
              {!user && <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 rounded-xl text-sm font-semibold text-pink-600 bg-pink-50">Sign in</Link>}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-16 border-t border-pink-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-pink-500" />
              <span className="font-display font-bold text-pink-600">ZI ZI'S WORLD</span>
            </div>
            <p className="text-sm text-slate-600 max-w-sm">A safer, calmer, more supportive world for every autism family. Sensory-friendly products, safety tools, and community.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-3 text-slate-800">Explore</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/shop">Shop</Link></li>
              <li><Link to="/resources">Resources</Link></li>
              <li><Link to="/community">Community</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3 text-slate-800">Support</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li><Link to="/insurance">Insurance</Link></li>
              <li><Link to="/donate">Donate</Link></li>
              <li><Link to="/fundraisers">Fundraisers</Link></li>
            </ul>
          </div>
        </div>
        <div className="text-center text-xs text-slate-500 py-4 border-t border-pink-100">
          &copy; {new Date().getFullYear()} Zi Zi's World. Accept. Understand. Support.
        </div>
      </footer>
    </div>
  );
}
